import { useEffect, useRef, useState } from "react";
import { Check, Trash2, X } from "react-feather";

import FormattedInput, {
  formatCurrencyInput,
  normalizeCommittedValue,
} from "../../CommonComponents/FormattedInput/FormattedInput";
import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";
import styles from "./EditableLimitCell.module.css";

function EditableLimitCell({
  initialValue,
  ariaLabel,
  formatter,
  currencySymbol,
  placeholder,
  onCommit,
  onClear,
  showClearButton = false,
}) {
  const isCurrencyFormatter = formatter === formatCurrencyInput;
  const resolvedFormatter = isCurrencyFormatter
    ? (value) => formatter(value, currencySymbol)
    : formatter;
  const safeInitial = resolvedFormatter
    ? resolvedFormatter(initialValue ?? "")
    : (initialValue ?? "");
  const [committedValue, setCommittedValue] = useState(safeInitial);
  const [value, setValue] = useState(safeInitial);
  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setCommittedValue(safeInitial);
    setValue(safeInitial);
  }, [safeInitial]);

  const isDirty = value !== committedValue;
  const hasClearableValue = Boolean(String(committedValue ?? "").trim());
  const showClearAction =
    showClearButton &&
    !isFocused &&
    !isDirty &&
    Boolean(onClear) &&
    (hasClearableValue || isClearing);

  useEffect(() => {
    if (!isDirty) return undefined;

    const handlePointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setValue(committedValue);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isDirty, committedValue]);

  const handleAccept = async () => {
    const committed = normalizeCommittedValue(value, {
      currency: isCurrencyFormatter,
    });

    if (onCommit) {
      setIsSaving(true);
      try {
        await onCommit(committed);
      } catch {
        setIsSaving(false);
        return;
      }
      setIsSaving(false);
    }

    setCommittedValue(committed);
    setValue(committed);
  };

  const handleReject = () => setValue(committedValue);

  const handleClear = async () => {
    const cleared = normalizeCommittedValue("", {
      currency: isCurrencyFormatter,
    });

    if (onClear) {
      setIsClearing(true);
      try {
        await onClear();
      } catch {
        setIsClearing(false);
        return;
      }
      setIsClearing(false);
    }

    setCommittedValue(cleared);
    setValue(cleared);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAccept();
    } else if (event.key === "Escape") {
      event.preventDefault();
      handleReject();
    }
  };

  return (
    <div
      className={`${styles.editable} sgp-risk-editable-limit-cell`}
      ref={wrapperRef}
    >
      <FormattedInput
        type="text"
        inputMode={formatter ? "decimal" : undefined}
        className={styles.input}
        value={value}
        placeholder={placeholder}
        formatter={resolvedFormatter}
        onChange={setValue}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        disabled={isSaving || isClearing}
      />
      {showClearAction && (
        <div className={styles.clearAction}>
          <button
            type="button"
            className={`${styles.iconButton} ${styles.clearButton}`}
            aria-label={`Clear ${ariaLabel}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleClear}
            disabled={isClearing}
            aria-busy={isClearing}
          >
            {isClearing ? (
              <RebetLoader height="14px" width="14px" />
            ) : (
              <Trash2 size={14} />
            )}
          </button>
        </div>
      )}
      {isDirty && (
        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.iconButton} ${styles.rejectButton}`}
            aria-label={`Reject ${ariaLabel} change`}
            onClick={handleReject}
            disabled={isSaving}
          >
            <X size={14} />
          </button>
          <button
            type="button"
            className={`${styles.iconButton} ${styles.acceptButton}`}
            aria-label={`Accept ${ariaLabel} change`}
            onClick={handleAccept}
            disabled={isSaving}
            aria-busy={isSaving}
          >
            {isSaving ? (
              <RebetLoader height="14px" width="14px" />
            ) : (
              <Check size={14} />
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default EditableLimitCell;
